import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import type { User } from '../types/user';

/** Get a user profile by UID. */
export async function getUser(uid: string): Promise<User | null> {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return null;
  return { uid: snap.id, ...snap.data() } as User;
}

/** Check if a nickname is already taken. */
export async function isNicknameTaken(nickname: string): Promise<boolean> {
  const q = query(collection(db, 'users'), where('nickname', '==', nickname));
  const snap = await getDocs(q);
  return !snap.empty;
}

/** Update user profile fields. */
export async function updateUser(uid: string, data: Partial<User>): Promise<void> {
  await updateDoc(doc(db, 'users', uid), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/** Update user location in the users collection. */
export async function updateUserLocation(
  uid: string,
  lat: number,
  lng: number,
  geohash: string,
): Promise<void> {
  await updateDoc(doc(db, 'users', uid), {
    currentLocation: {
      lat,
      lng,
      geohash,
      updatedAt: serverTimestamp(),
    },
    lastActiveAt: serverTimestamp(),
  });
}

/** Block a user. */
export async function blockUser(myUid: string, targetUid: string): Promise<void> {
  await updateDoc(doc(db, 'users', myUid), {
    blockedUserIds: arrayUnion(targetUid),
  });
}

/** Unblock a user. */
export async function unblockUser(myUid: string, targetUid: string): Promise<void> {
  await updateDoc(doc(db, 'users', myUid), {
    blockedUserIds: arrayRemove(targetUid),
  });
}

/** Change nickname (caller must check battery cost). */
export async function checkNicknameAvailable(nickname: string, myUid: string): Promise<boolean> {
  const q = query(collection(db, 'users'), where('nickname', '==', nickname));
  const snap = await getDocs(q);
  return snap.empty || snap.docs.every(d => d.id === myUid);
}

export async function changeNickname(uid: string, newNickname: string): Promise<void> {
  const taken = await isNicknameTaken(newNickname);
  if (taken) throw new Error('NICKNAME_TAKEN');
  await updateUser(uid, { nickname: newNickname } as Partial<User>);
}
